import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getBSPRegisterReport } from "./bspRegisterService";
import { FaTicketAlt, FaPrint, FaFileCsv, FaArrowLeft, FaFilter, FaUndo, FaPlane, FaEye } from "react-icons/fa";
import "./BSPRegister.css";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusBadgeClass = (status) => {
  switch (status) {
    case "Issued":
      return "bsp-badge-issued";
    case "Refunded":
      return "bsp-badge-refunded";
    case "Cancelled":
      return "bsp-badge-cancelled";
    default:
      return "bsp-badge-pending";
  }
};

const BSPRegisterPage = () => {
  const navigate = useNavigate();

  // ── Filters State ───────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [airline, setAirline] = useState("");
  const [ticketStatus, setTicketStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // ── Data State ──────────────────────────────────────────────
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [availableAirlines, setAvailableAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Detail Modal State ──────────────────────────────────────
  const [selectedTicket, setSelectedTicket] = useState(null);

  const hasFetched = useRef(false);

  // ── Fetch Report Data ───────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        airline: airline || undefined,
        ticket_status: ticketStatus !== "all" ? ticketStatus : undefined,
        search_query: searchQuery || undefined,
      };
      const data = await getBSPRegisterReport(filters);
      setTickets(data.tickets || []);
      setSummary(data.summary || null);
      setAvailableAirlines(data.available_airlines || []);
    } catch (err) {
      console.error("BSP Register fetch error:", err);
      setError("Failed to load BSP Ticket Register report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, airline, ticketStatus, searchQuery]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Handle Filter Triggers ──────────────────────────────────
  const handleApplyFilter = (e) => {
    if (e) e.preventDefault();
    fetchReport();
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setAirline("");
    setTicketStatus("all");
    setSearchQuery("");
    setTimeout(() => {
      fetchReport();
    }, 50);
  };

  // ── Print Report ────────────────────────────────────────────
  const handlePrint = () => {
    window.print();
  };

  // ── CSV Export ──────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!tickets.length) return;

    const headers = [
      "Ticket Number",
      "Issue Date",
      "Invoice No",
      "Passenger Name",
      "Customer Name",
      "Airline",
      "Flight No",
      "Sector",
      "Class",
      "Gross Fare",
      "Net Cost (BSP)",
      "Commission",
      "VAT",
      "Status",
    ];

    const rows = tickets.map((t) => [
      `"${t.ticket_number || ""}"`,
      `"${t.issue_date || ""}"`,
      `"${t.invoice_no || ""}"`,
      `"${t.passenger_name || ""}"`,
      `"${t.customer_name || ""}"`,
      `"${t.airline || ""}"`,
      `"${t.flight_number || ""}"`,
      `"${t.sector || ""}"`,
      `"${t.ticket_class || ""}"`,
      t.gross_amount,
      t.net_cost,
      t.commission,
      t.vat_amount,
      `"${t.status || ""}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `BSP_Register_Ticket_Report_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bsp-page">
      {/* ── Page Header ────────────────────────────────────── */}
      <div className="bsp-header">
        <div>
          <h2><FaTicketAlt className="me-2" /> BSP Register Ticket Report</h2>
          <div className="bsp-subtitle">
            Billing & Settlement Plan Air Ticket Sales, Payables & Commissions
          </div>
        </div>
        <div className="bsp-header-actions">
          <button className="bsp-btn bsp-btn-print" onClick={handlePrint}>
            🖨 Print Report
          </button>
          <button className="bsp-btn bsp-btn-export" onClick={handleExportCSV}>
            📥 Export CSV
          </button>
          <button
            className="bsp-btn bsp-btn-back"
            onClick={() => navigate("/dashboard")}
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ──────────────────────────────────── */}
      {summary && (
        <div className="bsp-summary-grid">
          <div className="bsp-card total-tickets">
            <div className="bsp-card-title">Total Tickets</div>
            <div className="bsp-card-value">{summary.total_tickets}</div>
            <div className="bsp-card-sub">
              Avg Ticket: AED {fmt(summary.avg_ticket_value)}
            </div>
          </div>

          <div className="bsp-card gross-sales">
            <div className="bsp-card-title">Total Gross Sales</div>
            <div className="bsp-card-value">AED {fmt(summary.total_gross_sales)}</div>
            <div className="bsp-card-sub">Selling Fare Collection</div>
          </div>

          <div className="bsp-card net-cost">
            <div className="bsp-card-title">Net Cost (BSP Payable)</div>
            <div className="bsp-card-value">AED {fmt(summary.total_net_cost)}</div>
            <div className="bsp-card-sub">Payable to Airlines</div>
          </div>

          <div className="bsp-card commission">
            <div className="bsp-card-title">Total Commission</div>
            <div className="bsp-card-value">AED {fmt(summary.total_commission)}</div>
            <div className="bsp-card-sub">Agency Profit Margin</div>
          </div>

          <div className="bsp-card vat-total">
            <div className="bsp-card-title">Total VAT Amount</div>
            <div className="bsp-card-value">AED {fmt(summary.total_vat)}</div>
            <div className="bsp-card-sub">Tax Amount</div>
          </div>
        </div>
      )}

      {/* ── Filters Section ────────────────────────────────── */}
      <div className="bsp-filter-card">
        <form onSubmit={handleApplyFilter} className="bsp-filter-grid">
          <div className="bsp-form-group">
            <label>Start Date</label>
            <input
              type="date"
              className="bsp-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="bsp-form-group">
            <label>End Date</label>
            <input
              type="date"
              className="bsp-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="bsp-form-group">
            <label>Airline</label>
            <select
              className="bsp-select"
              value={airline}
              onChange={(e) => setAirline(e.target.value)}
            >
              <option value="">All Airlines</option>
              {availableAirlines.map((air, idx) => (
                <option key={idx} value={air}>
                  {air}
                </option>
              ))}
            </select>
          </div>

          <div className="bsp-form-group">
            <label>Status</label>
            <select
              className="bsp-select"
              value={ticketStatus}
              onChange={(e) => setTicketStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="Issued">Issued</option>
              <option value="Pending">Pending</option>
              <option value="Refunded">Refunded</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="bsp-form-group">
            <label>Search Query</label>
            <input
              type="text"
              className="bsp-input"
              placeholder="Ticket #, Flight #, Passenger..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="bsp-filter-actions">
            <button type="submit" className="bsp-btn bsp-btn-print">
              <FaFilter className="me-1" /> Filter
            </button>
            <button
              type="button"
              className="bsp-btn bsp-btn-reset"
              onClick={handleReset}
            >
              <FaUndo className="me-1" /> Reset
            </button>
          </div>
        </form>
      </div>

      {/* ── Table Section ────────────────────────────────── */}
      <div className="bsp-table-card">
        {loading ? (
          <div className="bsp-loading">Loading BSP Register Ticket data...</div>
        ) : error ? (
          <div className="bsp-empty" style={{ color: "#ef4444" }}>
            {error}
          </div>
        ) : tickets.length === 0 ? (
          <div className="bsp-empty">
            No BSP tickets found matching the selected criteria.
          </div>
        ) : (
          <div className="bsp-table-wrapper">
            <table className="bsp-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Issue Date</th>
                  <th>Passenger</th>
                  <th>Airline & Flight</th>
                  <th>Sector</th>
                  <th>Class</th>
                  <th>Gross Fare</th>
                  <th>Net Cost</th>
                  <th>Commission</th>
                  <th>VAT</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t, idx) => (
                  <tr key={t.id || idx}>
                    <td>
                      <span className="bsp-ticket-code">{t.ticket_number}</span>
                      <br />
                      <small style={{ color: "#94a3b8" }}>{t.invoice_no}</small>
                    </td>
                    <td>{t.issue_date}</td>
                    <td>
                      <div className="bsp-pax-name">{t.passenger_name}</div>
                      <small style={{ color: "#64748b" }}>{t.customer_name}</small>
                    </td>
                    <td>
                      <div className="bsp-airline-tag">
                        <FaPlane className="me-1" /> {t.airline}
                      </div>
                      <small style={{ color: "#64748b", display: "block" }}>
                        {t.flight_number}
                      </small>
                    </td>
                    <td>{t.sector}</td>
                    <td>{t.ticket_class}</td>
                    <td style={{ fontWeight: 600 }}>AED {fmt(t.gross_amount)}</td>
                    <td>AED {fmt(t.net_cost)}</td>
                    <td style={{ color: "#16a34a", fontWeight: 600 }}>
                      AED {fmt(t.commission)}
                    </td>
                    <td>AED {fmt(t.vat_amount)}</td>
                    <td>
                      <span className={`bsp-badge ${statusBadgeClass(t.status)}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="bsp-btn-view"
                        onClick={() => setSelectedTicket(t)}
                      >
                        <FaEye className="me-1" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail Ticket Modal ────────────────────────────── */}
      {selectedTicket && (
        <div className="bsp-modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="bsp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bsp-modal-header">
              <h3><FaPlane className="me-2" /> Ticket Voucher Details</h3>
              <button
                className="bsp-modal-close"
                onClick={() => setSelectedTicket(null)}
              >
                &times;
              </button>
            </div>
            <div className="bsp-modal-body">
              <div className="bsp-detail-grid">
                <div className="bsp-detail-item">
                  <span className="label">Ticket Number</span>
                  <span className="val">{selectedTicket.ticket_number}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Invoice Ref</span>
                  <span className="val">{selectedTicket.invoice_no}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Issue Date</span>
                  <span className="val">{selectedTicket.issue_date}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Status</span>
                  <span className={`bsp-badge ${statusBadgeClass(selectedTicket.status)}`}>
                    {selectedTicket.status}
                  </span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Passenger Name</span>
                  <span className="val">{selectedTicket.passenger_name}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Customer Account</span>
                  <span className="val">{selectedTicket.customer_name}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Airline</span>
                  <span className="val">{selectedTicket.airline}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Flight Number</span>
                  <span className="val">{selectedTicket.flight_number}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Sector / Route</span>
                  <span className="val">{selectedTicket.sector}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Cabin Class</span>
                  <span className="val">{selectedTicket.ticket_class}</span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Gross Fare (Revenue)</span>
                  <span className="val" style={{ color: "#0f172a" }}>
                    AED {fmt(selectedTicket.gross_amount)}
                  </span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">BSP Net Cost (Payable)</span>
                  <span className="val" style={{ color: "#d97706" }}>
                    AED {fmt(selectedTicket.net_cost)}
                  </span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">Agency Commission</span>
                  <span className="val" style={{ color: "#16a34a" }}>
                    AED {fmt(selectedTicket.commission)}
                  </span>
                </div>

                <div className="bsp-detail-item">
                  <span className="label">VAT Amount</span>
                  <span className="val">AED {fmt(selectedTicket.vat_amount)}</span>
                </div>
              </div>
            </div>
            <div className="bsp-modal-footer">
              <button
                className="bsp-btn bsp-btn-print"
                onClick={() => window.print()}
              >
                <FaPrint className="me-1" /> Print Ticket
              </button>
              <button
                className="bsp-btn bsp-btn-back"
                onClick={() => setSelectedTicket(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BSPRegisterPage;
